// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { IntegerFieldDefinition, LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries, { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden/VisuallyHidden";
import Img, { ImgProps } from "@store/MobiusOverrides/Img/Img";
import { linkStyles as mixinLinkStyles } from "@store/Themes/common/mixins/link";
import { contentSpacingStyles } from "@store/Themes/common/mixins/spacing";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    direction = "direction",
    wrapperSpacingBottom = "wrapperSpacingBottom",
    spaceBetweenIconAndText = "spaceBetweenIconAndText",
    alignment = "alignment",
    title = "title",
    titleLink = "titleLink",
    links = "links",
    icon = "icon",
    textColor = "textColor",
    isTextBold = "isTextBold",
    showOnlyIcon = "showOnlyIcon",
    iconSize = "iconSize",
    iconMinHeight = "iconMinHeight",
    backgroundColor = "backgroundColor",
    linksPerRow = "linksPerRow",
    isBreakpointsActivated = "isBreakpointsActivated",
    xs = "xs",
    sm = "sm",
    md = "md",
    lg = "lg",
    xl = "xl",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.direction]: "vertical" | "horizontal";
        [fields.wrapperSpacingBottom]: number;
        [fields.spaceBetweenIconAndText]: number;
        [fields.alignment]: "left" | "top";
        [fields.iconSize]: number;
        [fields.iconMinHeight]: number;
        [fields.linksPerRow]: number;
        [fields.icon]: string;
        [fields.textColor]: string;
        [fields.isTextBold]: boolean;
        [fields.showOnlyIcon]: boolean;
        [fields.links]: LinkModel[];
        [fields.isBreakpointsActivated]: boolean;
        [fields.xs]: number;
        [fields.sm]: number;
        [fields.md]: number;
        [fields.lg]: number;
        [fields.xl]: number;
    };
}

interface LinkModel {
    fields: {
        title: string;
        icon: string;
        openInNewWindow: boolean;
        destination: LinkFieldValue;
        overriddenTitle: string;
    };
}

interface WrapperContentStyles extends InjectableCss {
    typographyProps?: TypographyPresentationProps;
}

export interface IconsLinksListStyles {
    iconsLinksWrapper?: InjectableCss;
    linkWrapper?: InjectableCss;
    link?: LinkPresentationProps;
}

const styles: IconsLinksListStyles = {
    iconsLinksWrapper: {
        css: css`
            width: 100%;
            padding: 0;
            display: flex;
            flex-wrap: wrap;
            align-items: flex-start;
        `,
    },
    linkWrapper: {
        css: css`
            display: flex;
            align-items: center;
            padding: 0;
            flex-shrink: 0;
            margin: 0;
        `,
    },
    link: {
        typographyProps: {
            color: "text.main",
        },
    },
};

export const iconsLinksListStyles = styles;

export const IconsLinks: FC<OwnProps> = ({ fields }) => {
    // const links = useGetLinks(fields.links, o => o.fields.destination); // TODO -- Fix this

    const iconsLinksWrapperStyle = css`
        ${fields.direction === "horizontal" ? "flex-direction: row;" : "flex-direction: column;"}
        ${styles.iconsLinksWrapper?.css}
    `;

    const linkWrapperSpacing = 8;
    const linkWrapperStylesByBreakpoint = (numberOfItems: number) => css`
        width: calc(${100 / numberOfItems}% - ${pxToRem(linkWrapperSpacing * 2)});
        &:nth-child(${numberOfItems}n) {
            margin-right: 0;
        }
        &:nth-child(${numberOfItems}n+1) {
            margin-left: 0;
        }
    `;

    const responsiveLinkWrapperStyles = css`
        ${linkWrapperStylesByBreakpoint(fields.xl)}

        ${({ theme }) =>
            breakpointMediaQueries(theme, [
                css`
                    ${linkWrapperStylesByBreakpoint(fields.xs)}
                `,
                css`
                    ${linkWrapperStylesByBreakpoint(fields.sm)}
                `,
                css`
                    ${linkWrapperStylesByBreakpoint(fields.md)}
                `,
                css`
                    ${linkWrapperStylesByBreakpoint(fields.lg)}
                `,
                css`
                    ${linkWrapperStylesByBreakpoint(fields.xl)}
                `,
            ])}
    `;

    const linkWrapperStyles: InjectableCss = {
        ...styles.linkWrapper,
        css: css`
            display: flex;
            align-items: flex-start;
            justify-content: center;
            ${fields.alignment === "top" &&
            `
                display: flex;
                justify-content: center;
            `}
            ${fields.direction === "horizontal"
                ? css`
                      width: calc(${100 / fields.linksPerRow}% - ${pxToRem(linkWrapperSpacing * 2)});
                      ${contentSpacingStyles(
                          0,
                          pxToRem(linkWrapperSpacing),
                          pxToRem(linkWrapperSpacing),
                          pxToRem(linkWrapperSpacing),
                      )}
                      ${fields.isBreakpointsActivated && responsiveLinkWrapperStyles}
                  `
                : css`
                      width: 100%;
                      ${contentSpacingStyles(pxToRem(linkWrapperSpacing), 0, pxToRem(linkWrapperSpacing), 0)}
                  `};
        `,
    };

    const textColor = fields.textColor ? fields.textColor : "text.main";

    const linkStyles: LinkPresentationProps = {
        ...styles.link,
        css: css`
            ${mixinLinkStyles(textColor, textColor, false, false)}
            margin-bottom: ${pxToRem(fields.wrapperSpacingBottom)};
            ${fields.isTextBold &&
            css`
                font-weight: ${({ theme }) => theme.design.typography.weight.bold};
            `}
        `,
        typographyProps: {
            ...styles.link?.typographyProps,
            color: fields.textColor || undefined,
            css: css`
                display: flex;
                align-items: center;
                ${fields.alignment === "top" ? "flex-direction: column;" : null}
            `,
        },
    };

    const wrapperContent: WrapperContentStyles = {
        css: css`
            ${linkStyles.css}

            &:hover {
                text-decoration: none;
            }

            ${fields.alignment === "left" &&
            `
                display: flex;
                align-items: center;
            `}
        `,
        typographyProps: {
            ...linkStyles.typographyProps,
            css: css`
                ${linkStyles.typographyProps?.css}

                &:hover {
                    text-decoration: none;
                }
            `,
        },
    };

    const imgStyles: ImgProps = {
        css: css`
            display: block;
            width: ${pxToRem(fields.iconSize)};
            height: auto;
            ${!fields.showOnlyIcon && css``}
            ${fields.direction === "horizontal" && `min-height: ${fields.iconMinHeight}px`};
            ${fields.alignment === "left"
                ? `
                    margin-right: ${pxToRem(fields.spaceBetweenIconAndText)};
                `
                : `
                    margin-bottom: ${pxToRem(fields.spaceBetweenIconAndText)};
                `}
        `,
    };

    const linkContent = (
        index: number,
        link: {
            url: string | undefined;
            title: string | undefined;
        },
    ) => (
        <>
            <Img src={fields.links[index].fields.icon} altText={link.title} {...imgStyles} />
            {link.url ? (
                !fields.showOnlyIcon && (fields.links[index].fields.title || link.title)
            ) : (
                <Typography {...wrapperContent.typographyProps}>
                    {!fields.showOnlyIcon && (fields.links[index].fields.title || link.title)}
                </Typography>
            )}
            <VisuallyHidden>
                {fields.links[index].fields.title !== "" ? fields.links[index].fields.title : link.title}
            </VisuallyHidden>
        </>
    );

    return (
        <StyledWrapper css={iconsLinksWrapperStyle}>
            {/* {links.map((link, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <StyledWrapper key={index} {...linkWrapperStyles}>
                    {link.url ? (
                        <Link
                            {...linkStyles}
                            href={link.url}
                            target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                        >
                            {linkContent(index, link)}
                        </Link>
                    ) : (
                        <StyledWrapper css={wrapperContent.css}>{linkContent(index, link)}</StyledWrapper>
                    )}
                </StyledWrapper>
            ))} */}
        </StyledWrapper>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 1,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 2,
};

const baseBreakpointsFields = (name: string): IntegerFieldDefinition => ({
    name,
    editorTemplate: "IntegerField",
    fieldType: "General",
    defaultValue: 1,
    isRequired: true,
    tab: settingsTab,
    isVisible: ({ fields }) => fields.direction === "horizontal" && fields.isBreakpointsActivated,
});

const definition: WidgetDefinition = {
    group: "Basic",
    icon: "Link",
    displayName: "Icons Links",
    isSystem: true,
    fieldDefinitions: [
        {
            name: fields.links,
            editorTemplate: "ListField",
            tab: contentTab,
            getDisplay: (item: HasFields) => {
                return item.fields.title;
            },
            defaultValue: [],
            fieldType: "Translatable",
            fieldDefinitions: [
                {
                    name: "title",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
                {
                    name: fields.icon,
                    displayName: "Icon",
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                },
                {
                    name: "destination",
                    editorTemplate: "LinkField",
                    defaultValue: {
                        value: "",
                        type: "Url",
                    },
                },
                {
                    name: "openInNewWindow",
                    editorTemplate: "CheckboxField",
                    defaultValue: false,
                },
            ],
        },
        {
            name: fields.direction,
            displayName: "Display",
            editorTemplate: "RadioButtonsField",
            defaultValue: "horizontal",
            fieldType: "General",
            tab: settingsTab,
            options: [
                {
                    displayName: "Column",
                    value: "vertical",
                },
                {
                    displayName: "Row",
                    value: "horizontal",
                },
            ],
        },
        {
            name: fields.alignment,
            displayName: "Icon Position",
            editorTemplate: "RadioButtonsField",
            defaultValue: "left",
            fieldType: "General",
            tab: settingsTab,
            options: [
                {
                    displayName: "Left of Text",
                    value: "left",
                },
                {
                    displayName: "Top of Text",
                    value: "top",
                },
            ],
        },
        {
            name: fields.wrapperSpacingBottom,
            displayName: "Wrapper Spacing Bottom",
            editorTemplate: "IntegerField",
            defaultValue: 30,
            min: 0,
            fieldType: "General",
            tab: settingsTab,
        },
        {
            name: fields.spaceBetweenIconAndText,
            displayName: "Space Between Icon and Text",
            editorTemplate: "IntegerField",
            defaultValue: 0,
            min: 0,
            fieldType: "General",
            tab: settingsTab,
        },
        {
            name: fields.iconSize,
            displayName: "Icons Size",
            editorTemplate: "IntegerField",
            defaultValue: 30,
            min: 1,
            fieldType: "General",
            tab: settingsTab,
        },
        {
            name: fields.iconMinHeight,
            displayName: "Icons Minimum Height",
            editorTemplate: "IntegerField",
            defaultValue: 30,
            min: 1,
            fieldType: "General",
            tab: settingsTab,
        },
        {
            name: fields.textColor,
            fieldType: "General",
            displayName: "Text Color",
            editorTemplate: "ColorPickerField",
            defaultValue: "",
            tab: settingsTab,
        },
        {
            name: fields.isTextBold,
            displayName: "Set the text bold",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
            tab: settingsTab,
        },
        {
            name: fields.showOnlyIcon,
            displayName: "Show Only Icon",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
            tab: settingsTab,
        },
        {
            name: fields.isBreakpointsActivated,
            displayName: "Activate Number of Items for Each Breakpoints",
            editorTemplate: "CheckboxField",
            variant: "toggle",
            fieldType: "General",
            defaultValue: false,
            isRequired: true,
            isVisible: ({ fields }) => fields.direction === "horizontal",
            tab: settingsTab,
        },
        {
            name: fields.linksPerRow,
            displayName: "Icons Per Row",
            fieldType: "General",
            editorTemplate: "DropDownField",
            options: [
                { value: 1 },
                { value: 2 },
                { value: 3 },
                { value: 4 },
                { value: 5 },
                { value: 6 },
                { value: 7 },
                { value: 8 },
            ],
            defaultValue: 8,
            isVisible: ({ fields }) => fields.direction === "horizontal" && !fields.isBreakpointsActivated,
            tab: settingsTab,
        },
        {
            ...baseBreakpointsFields(fields.xs),
            displayName: "0 - 600px",
        },
        {
            ...baseBreakpointsFields(fields.sm),
            displayName: "600px - 900px",
        },
        {
            ...baseBreakpointsFields(fields.md),
            displayName: "900px - 1200px",
        },
        {
            ...baseBreakpointsFields(fields.lg),
            displayName: "1200px - 1500px",
        },
        {
            ...baseBreakpointsFields(fields.xl),
            displayName: "1500+",
        },
    ],
};

const widgetModule: WidgetModule = {
    component: IconsLinks,
    definition,
};

export default widgetModule;
